/**
 * Helpers for values that are a calendar DAY rather than an instant — a
 * maintenance due date, "owned since", a public-record sale date. The API
 * stores these at noon (or midnight) UTC, so they must be read with UTC
 * getters: in any US timezone, local getters turn a midnight-UTC Oct 20 into
 * Oct 19.
 */

export type CalendarDay = { y: number; m: number; d: number }; // m is 0-based

/** UTC calendar day of an ISO string / Date, or null when it isn't a date. */
export const toCalendarDay = (value?: string | Date | null): CalendarDay | null => {
  if (!value) return null;
  const date = value instanceof Date ? value : new Date(value);
  if (Number.isNaN(date.getTime())) return null;
  return { y: date.getUTCFullYear(), m: date.getUTCMonth(), d: date.getUTCDate() };
};

/** The viewer's own "today" as a calendar day. */
export const todayDay = (now: Date = new Date()): CalendarDay => ({
  y: now.getFullYear(),
  m: now.getMonth(),
  d: now.getDate(),
});

/** Comparable integer (YYYYMMDD) for sorting and comparing days. */
export const dayKey = (day: CalendarDay): number => day.y * 10000 + (day.m + 1) * 100 + day.d;

/** "YYYY-MM-DD" — what <input type="date"> and the API both speak. */
export const toInputValue = (day: CalendarDay | null): string =>
  day
    ? `${day.y}-${String(day.m + 1).padStart(2, "0")}-${String(day.d).padStart(2, "0")}`
    : "";

const asUTCDate = (day: CalendarDay) => new Date(Date.UTC(day.y, day.m, day.d, 12));

/** "Oct 20, 2026" */
export const formatDay = (
  value?: string | Date | null,
  opts: Intl.DateTimeFormatOptions = { month: "short", day: "numeric", year: "numeric" }
): string => {
  const day = toCalendarDay(value);
  return day ? asUTCDate(day).toLocaleDateString("en-US", { ...opts, timeZone: "UTC" }) : "";
};

/** "September 2012" */
export const formatMonthYear = (value?: string | Date | null): string =>
  formatDay(value, { month: "long", year: "numeric" });

/** Whole days from `from` to `to` (negative when `to` is earlier). */
export const daysBetween = (from: CalendarDay, to: CalendarDay): number =>
  Math.round((asUTCDate(to).getTime() - asUTCDate(from).getTime()) / 86_400_000);

/** Add whole months, clamping to month end (Jan 31 + 1 = Feb 28/29). */
export const addMonthsDay = (day: CalendarDay, months: number): CalendarDay => {
  const total = day.m + months;
  const y = day.y + Math.floor(total / 12);
  const m = ((total % 12) + 12) % 12;
  const lastDay = new Date(Date.UTC(y, m + 1, 0)).getUTCDate();
  return { y, m, d: Math.min(day.d, lastDay) };
};

export type OwnershipDuration = {
  years: number;
  months: number;
  totalMonths: number;
  /** It's the start month and at least a year has passed — celebrate. */
  isAnniversaryMonth: boolean;
};

/**
 * How long someone has owned the home, as of `today`. Null when the start is
 * in the future (bad data) — callers should hide the card rather than show
 * a negative duration.
 */
export const ownershipDuration = (
  start: CalendarDay,
  today: CalendarDay = todayDay()
): OwnershipDuration | null => {
  if (dayKey(start) > dayKey(today)) return null;
  let totalMonths = (today.y - start.y) * 12 + (today.m - start.m);
  if (today.d < start.d) totalMonths -= 1;
  totalMonths = Math.max(0, totalMonths);
  const years = Math.floor(totalMonths / 12);
  return {
    years,
    months: totalMonths % 12,
    totalMonths,
    isAnniversaryMonth: today.m === start.m && today.y > start.y,
  };
};

const plural = (n: number, word: string) => `${n} ${word}${n === 1 ? "" : "s"}`;

/** "13 years, 1 month" / "8 months" / "less than a month" */
export const formatDuration = ({ years, months }: OwnershipDuration): string => {
  if (years === 0 && months === 0) return "less than a month";
  if (years === 0) return plural(months, "month");
  if (months === 0) return plural(years, "year");
  return `${plural(years, "year")}, ${plural(months, "month")}`;
};
